import { useEffect, useState, useMemo } from 'react'
import { studentsStore } from 'pages/Students/model/studentsStore'
import { fetchStudentCareerAssessment } from 'api/users'
import { classroomStore } from 'pages/Classroom/model/classroomStore'
import { shouldRefetchDetails } from 'pages/Classroom/utils/taskFreshness'

// Hook for CareerQuizSubmission component
export const useCareerQuizSubmission = ({ assignment, student }) => {
	const studentId = student?.id
	const [personalityType, setPersonalityType] = useState(null)
	const [isLoadingPersonality, setIsLoadingPersonality] = useState(false)

	// Get student data from StudentsStore
	const studentData = useMemo(() => {
		if (!studentId) return null
		return studentsStore.getStudentById(studentId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [studentId, studentsStore.students])

	useEffect(() => {
		if (!studentId) return
		const taskId = assignment?.id || assignment?.task_id
		const freshnessMeta = classroomStore.getTaskFreshnessMeta({
			taskId,
			studentId,
		})
		if (!shouldRefetchDetails({ type: 'career_quiz', meta: freshnessMeta })) {
			return
		}
		setIsLoadingPersonality(true)
		fetchStudentCareerAssessment(studentId)
			.then(data => {
				const personality =
					data?.dominantType ||
					data?.personalityType ||
					data?.assessment?.dominantType ||
					data?.assessment?.personalityType ||
					null
				if (personality) {
					if (typeof personality === 'string') {
						setPersonalityType({ name: personality })
					} else {
						setPersonalityType(personality)
					}
				}
				classroomStore.markTaskDetailsLoaded({
					taskId,
					studentId,
					type: 'career_quiz',
				})
			})
			.catch(error => {
				console.error('Error fetching personality type:', error)
			})
			.finally(() => {
				setIsLoadingPersonality(false)
			})
	}, [assignment?.id, assignment?.task_id, studentId])

	// Extract careers from student data
	// career field can be a string (single career) or potentially a JSON string with array
	const careers = useMemo(() => {
		if (!studentData?.career) return []
		
		const careerValue = studentData.career
		
		// If it's already an array, return it
		if (Array.isArray(careerValue)) {
			return careerValue
		}
		
		// If it's a string, try to parse as JSON first (in case it's a JSON string)
		if (typeof careerValue === 'string') {
			try {
				const parsed = JSON.parse(careerValue)
				if (Array.isArray(parsed)) {
					return parsed
				}
			} catch (e) {
				// Not a JSON string, treat as single career string
			}
			// Single career as string - convert to array
			return [careerValue]
		}
		
		// If it's an object, try to extract name or convert to array
		if (typeof careerValue === 'object' && careerValue !== null) {
			return [careerValue]
		}
		
		return []
	}, [studentData?.career])

	const isLoading = false // Data comes from store, no separate loading state
	const error = null

	// Get response text from student data
	const responseText =
		student?.response_text ||
		student?.responseText ||
		student?.assignmentData?.response_text ||
		assignment?.students?.find(s => s.id === student?.id)?.response_text ||
		''

	return {
		careers,
		isLoading,
		error,
		personalityType,
		isLoadingPersonality,
		responseText,
		hasResponse: Boolean(responseText && responseText.trim()),
	}
}

export default useCareerQuizSubmission
